import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

export interface IdleWarningDialogData {
  getRemainingMs: () => number;
}

export type IdleWarningDialogResult = 'stay' | 'logout';

@Component({
  selector: 'app-idle-warning-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex cursor-pointer flex-col gap-4 p-6" (click)="close('stay')">
      <div class="flex items-center gap-3">
        <mat-icon class="text-amber-500">schedule</mat-icon>
        <h2 class="m-0 text-lg font-semibold text-gray-900">Are you still there?</h2>
      </div>
      <p class="m-0 text-gray-700">
        You have been inactive for a while. For your security you will be signed out in
        <span class="font-bold text-red-600">{{ remainingSeconds() }}</span>
        second{{ remainingSeconds() === 1 ? '' : 's' }}.
      </p>
      <div class="flex justify-end gap-3">
        <button mat-stroked-button type="button" (click)="$event.stopPropagation(); close('logout')">
          Sign out now
        </button>
        <button mat-flat-button color="primary" type="button" cdkFocusInitial>Stay signed in</button>
      </div>
    </div>
  `,
})
export class IdleWarningDialogComponent {
  private _data = inject<IdleWarningDialogData>(MAT_DIALOG_DATA);
  private _dialogRef = inject<MatDialogRef<IdleWarningDialogComponent, IdleWarningDialogResult>>(MatDialogRef);

  readonly remainingSeconds = signal(this._secondsLeft());

  constructor() {
    const timer = setInterval(() => this.remainingSeconds.set(this._secondsLeft()), 1000);
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  close(result: IdleWarningDialogResult): void {
    this._dialogRef.close(result);
  }

  private _secondsLeft(): number {
    return Math.max(0, Math.ceil(this._data.getRemainingMs() / 1000));
  }
}
